import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {FAIRY_LIGHTS_KIND} from '../../../configs/biomeEffects/fairyLights';
import {resolveSpawnBox} from './biomeContext';
import {
  TWO_PI,
  clamp01,
  hash01,
  lerp,
  mixTints,
  pickPointInBox,
  pickWeighted,
  randomIn,
  randomSign,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ОГОНЬКИ ФЕЙ И СВЕТ ВОЛШЕБНОГО ЛЕСА. Чистые функции без Phaser и DOM: по этому
 * описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * Дорожек две: полет огонька и свет нарисованных мест.
 *
 * ПОЛЕТ. Середина петли плывет над поляной по пологой дуге — от линии деревьев
 * через точку, на которую смотрит камера, к дальней кромке. Огонек кружит
 * вокруг этой середины быстрее, чем она плывет, поэтому его путь — цепочка
 * петель, как росчерк пера. Чтобы петли не шли ровной пружиной, радиус петли
 * дышит, а сама петля медленно ходит по встречному кругу. Весь путь — функция
 * времени: `точка(время)`.
 *
 * ЖИЗНЬ ОГОНЬКА короче перелета: дугу он проходит не всю — от половины до
 * двух третей — и последнюю долю жизни плавно гаснет над поляной. Вылетает он
 * с той кромки, что ближе к точке камеры: с дальней он погас бы, не добравшись
 * до кадра. Срок жизни подбирается так, чтобы мимо точки камеры огонек
 * пролетел, еще не начав гаснуть; когда камера смотрит в самую середину
 * поляны, на это не хватает и двух третей дуги — туда он долетает, уже
 * померкнув на треть.
 *
 * ОГОНЕК — голова, шлейф и пыльца. Голова: звездочка, ядро и цветной ореол в
 * `точка(время)`. Шлейф — пятна в `точка(время - отставание)`: это копии головы
 * из недавнего прошлого, поэтому шлейф сам повторяет изгиб петли. Пыльца —
 * искры, которые огонек роняет через равные промежутки: искра рождается в
 * `точка(миг рождения)` и остается там — расплывается, оседает и тает. Миг
 * рождения искры считается из времени, так что и ее поза — функция времени.
 *
 * ПАРА — второй огонек на том же пути с отставанием в полпетли: он гонится за
 * первым по его же петлям, вылетает из леса следом и гаснет следом.
 *
 * Огоньки светятся (слой с наложением «свет») и летят над отрядами: они
 * мелкие, полет один на все поле.
 *
 * ПАУЗА МЕЖДУ ПОЛЕТАМИ ВХОДИТ В СРОК ГРУППЫ. Полет длится по-разному (хорда
 * поляны бывает разной длины), а расписание контроллера считает промежуток от
 * НАЧАЛА предыдущей группы — паузу «от погасшего огонька до нового вылета» им
 * не задать. Поэтому отлетавшая группа доживает свою паузу невидимой, а новая
 * рождается, как только прежняя ушла (группа на дорожке одна).
 *
 * СВЕТ ЛЕСА — постоянная группа: по мягкому пятну на каждое нарисованное
 * светящееся место (кристаллы, кроны, цветы и пятна мха на поляне). Пятно
 * дышит — у каждого свой срок и своя фаза — и время от времени «просыпается»:
 * ненадолго разгорается ярче. Проснется ли место на очередном круге и насколько
 * ярко, решает число, которое зависит только от места и номера круга: поза
 * остается функцией времени. Места светятся все и всегда — выбирать среди них
 * по камере незачем.
 */

const STAR = 'star';
const CORE = 'core';
const HALO = 'halo';
const TRAIL = 'trail';
const DUST = 'dust';
const GLOW = 'glow';

const WHITE = 0xffffff;

// Промежуток расписания здесь — не пауза (она в сроке группы), а лишь то, как
// скоро дорожка начинает проверять, ушла ли прежняя группа
const NEXT_FLIGHT_CHECK_MS = 500;

// На столько отрезков делится дуга пути, когда меряется ее длина
const PATH_LENGTH_STEPS = 32;
// Второе мерцание звездочки — на несоизмеримой частоте (золотое сечение):
// вместе с первым оно не складывается в ровное биение
const TWINKLE_SECOND_RATE = 1.618;
const TWINKLE_FIRST_SHARE = 0.65;
// Самая вялая искра пыльцы отплывает на такую долю разлета самой бойкой
const DUST_MIN_SPREAD_SHARE = 0.35;

/** Точка пологой дуги (квадратичной кривой Безье): share — доля пути, 0..1 */
const resolveArcPoint = (from, control, to, share, axis) => {
  const rest = 1 - share;
  return rest * rest * from[axis] + 2 * rest * share * control[axis] + share * share * to[axis];
};

const createTracks = (context, params) => {
  const {hexSize, side} = context;
  const glade = context.toWorldOval(params.glade);
  const {life, loop, head, trail, dust, glows} = params;
  const trailSpanMs = trail.count * trail.stepMs;
  // Место искры пыльцы освобождается под следующую через столько
  const dustCycleMs = dust.count * dust.shedEveryMs;

  // -------------------------------------------------------------------- полет

  /**
   * Путь середины петли: хорда поляны через точку, на которую смотрит камера,
   * прогнутая в дугу. Считается в долях полуосей поляны (поляна — единичный
   * круг), затем переводится в мир. passShare — на какой доле хорды лежит
   * точка камеры, считая от вылета.
   */
  const buildArc = (rng, view) => {
    const through = pickPointInBox(rng, resolveSpawnBox(context, view, {fieldMarginFactor: 0, viewMarginFactor: 0}));
    let throughX = (through.x - glade.x) / glade.radiusX;
    let throughY = (through.y - glade.y) / glade.radiusY;
    // Камера смотрит на край поляны или мимо нее — точка подтягивается к середине
    const distance = Math.hypot(throughX, throughY);
    if (distance > params.throughShare) {
      throughX *= params.throughShare / distance;
      throughY *= params.throughShare / distance;
    }

    // Хорда в случайную сторону: два пересечения прямой с линией деревьев
    const heading = rng() * TWO_PI;
    let directionX = Math.cos(heading);
    let directionY = Math.sin(heading);
    let along = throughX * directionX + throughY * directionY;
    // Вылет — с ближней к точке камеры кромки: огонек живет недолго и гаснет,
    // не долетев до дальней. С дальней он погас бы, не добравшись до кадра
    if (along > 0) {
      directionX = -directionX;
      directionY = -directionY;
      along = -along;
    }
    const reach = Math.sqrt(
      along * along - (throughX * throughX + throughY * throughY) + params.entryShare * params.entryShare
    );
    const from = {x: throughX + directionX * (-along - reach), y: throughY + directionY * (-along - reach)};
    const to = {x: throughX + directionX * (-along + reach), y: throughY + directionY * (-along + reach)};
    // Прогиб — от середины хорды поперек нее: дуга симметрична, и середина
    // петли плывет по ней почти с ровной скоростью
    const bow = randomSign(rng) * randomIn(rng, params.bowShare) * reach * 2;
    const control = {x: (from.x + to.x) / 2 - directionY * bow, y: (from.y + to.y) / 2 + directionX * bow};

    const toWorld = (point) => ({x: glade.x + point.x * glade.radiusX, y: glade.y + point.y * glade.radiusY});
    return {
      from: toWorld(from), control: toWorld(control), to: toWorld(to), passShare: (along + reach) / (reach * 2),
    };
  };

  const resolveArcLength = ({from, control, to}) => {
    let length = 0;
    let previousX = from.x;
    let previousY = from.y;
    for (let step = 1; step <= PATH_LENGTH_STEPS; step += 1) {
      const x = resolveArcPoint(from, control, to, step / PATH_LENGTH_STEPS, 'x');
      const y = resolveArcPoint(from, control, to, step / PATH_LENGTH_STEPS, 'y');
      length += Math.hypot(x - previousX, y - previousY);
      previousX = x;
      previousY = y;
    }
    return length;
  };

  /** Частицы одного огонька: ореол, шлейф, пыльца, ядро и звездочка */
  const buildFairy = (rng, tint, lagMs) => {
    const base = {layer: FIELD_BIOME_LAYER_KEYS.AIR_GLOW, lagMs};
    const twinkle = {
      twinklePhase: rng() * TWO_PI,
      twinkleSecondPhase: rng() * TWO_PI,
    };
    const particles = [{
      ...base, ...twinkle, kind: HALO, texture: FIELD_BIOME_TEXTURES.DOT, tint,
    }];
    for (let index = 1; index <= trail.count; index += 1) {
      // От 1 у самой головы до нуля за хвостом
      const strength = 1 - index / (trail.count + 1);
      particles.push({
        ...base,
        kind: TRAIL,
        texture: FIELD_BIOME_TEXTURES.DOT,
        tint,
        lagMs: lagMs + index * trail.stepMs,
        size: hexSize * lerp(trail.sizeFactor.tail, trail.sizeFactor.head, strength),
        alpha: trail.alpha * strength * strength,
      });
    }
    for (let slot = 0; slot < dust.count; slot += 1) {
      particles.push({
        ...base,
        kind: DUST,
        texture: FIELD_BIOME_TEXTURES.DOT,
        // Пыльца — той же краски, что и огонек
        tint,
        // Место искры: первая рождается в миг slot * shedEveryMs, следующие —
        // через круг (dustCycleMs) каждая
        slot,
        // У каждого места — свой ряд жребиев: срок, размер и снос искры на
        // каждом круге другие
        seed: rng() * 1000,
        twinklePhase: rng() * TWO_PI,
      });
    }
    particles.push({
      ...base,
      ...twinkle,
      kind: CORE,
      texture: FIELD_BIOME_TEXTURES.DOT,
      tint: mixTints(WHITE, tint, head.coreTintShare),
    });
    particles.push({
      ...base,
      ...twinkle,
      kind: STAR,
      texture: FIELD_BIOME_TEXTURES.SPARK,
      tint: mixTints(WHITE, tint, head.starTintShare),
      spinPhase: rng() * TWO_PI,
      spinRadPerMs: (toRadians(head.starSpinDegPerSecond) / 1000) * randomSign(rng),
    });
    return particles;
  };

  const buildFlight = ({rng, view}) => {
    const arc = buildArc(rng, view);
    const radius = hexSize * randomIn(rng, loop.radiusFactor);
    const periodMs = randomIn(rng, loop.periodMs);
    // Петля плывет над поляной во столько раз медленнее, чем огонек бежит по ней
    const driftPerMs = (TWO_PI * radius) / periodMs / randomIn(rng, loop.speedRatio);
    // За столько петля проплыла бы всю дугу, от кромки до кромки
    const crossingMs = resolveArcLength(arc) / driftPerMs;
    // Огонек живет долю перелета — такую, чтобы мимо точки камеры он пролетел
    // на заданной доле своей жизни, еще не начав гаснуть, но в заданных пределах
    const lifeShare = Math.min(life.share.max, Math.max(life.share.min, arc.passShare / life.passAtShare));
    const flightMs = crossingMs * lifeShare;
    const tiltRad = rng() * TWO_PI;

    const isPair = rng() < params.pairShare;
    const first = pickWeighted(rng, params.palette, ({weight}) => weight);
    // В паре краски разные
    const second = isPair
      ? pickWeighted(rng, params.palette.filter((colour) => colour !== first), ({weight}) => weight)
      : null;
    const pairLagMs = isPair ? periodMs * randomIn(rng, params.pairLagShare) : 0;
    const particles = buildFairy(rng, first.tint, 0);
    if (isPair) {
      particles.push(...buildFairy(rng, second.tint, pairLagMs));
    }

    // Последними догорают хвост шлейфа и пыльца отставшего огонька
    const visibleMs = flightMs + pairLagMs + Math.max(trailSpanMs, dust.lifeMs.max);
    return {
      durationMs: visibleMs + randomIn(rng, params.pauseMs),
      visibleMs,
      flightMs,
      crossingMs,
      lifeShare,
      fadeOutMs: flightMs * life.fadeOutShare,
      isPair,
      colours: isPair ? [first.id, second.id] : [first.id],
      from: arc.from,
      control: arc.control,
      to: arc.to,
      radius,
      periodMs,
      turn: randomSign(rng),
      phase: rng() * TWO_PI,
      tiltCos: Math.cos(tiltRad),
      tiltSin: Math.sin(tiltRad),
      breathePeriodMs: periodMs * randomIn(rng, loop.breathePeriods),
      breathePhase: rng() * TWO_PI,
      swayRadius: radius * randomIn(rng, loop.swayShare),
      swayPeriodMs: periodMs * randomIn(rng, loop.swayPeriods),
      swayPhase: rng() * TWO_PI,
      particles,
    };
  };

  /**
   * Где огонек в момент своего полета: середина петли на дуге плюс место на
   * петле. Пишет x и y в out — расчет идет каждый кадр на каждую частицу
   */
  const writeFlightPoint = (group, flightTimeMs, out) => {
    // Доля всей дуги: жизнь огонька кончается раньше, чем дуга
    const share = clamp01(flightTimeMs / group.crossingMs);
    // Петля раскручивается от линии деревьев
    const spiral = smoothstep(0, loop.spiralMs, flightTimeMs);
    const breathe = 1
      + loop.breatheDepth * Math.sin((TWO_PI * flightTimeMs) / group.breathePeriodMs + group.breathePhase);
    const radius = group.radius * spiral * breathe;
    const angle = group.phase + (group.turn * TWO_PI * flightTimeMs) / group.periodMs;
    // Сама петля ходит по медленному встречному кругу
    const swayRadius = group.swayRadius * spiral;
    const swayAngle = group.swayPhase - (group.turn * TWO_PI * flightTimeMs) / group.swayPeriodMs;
    const loopX = radius * Math.cos(angle) + swayRadius * Math.cos(swayAngle);
    const loopY = (radius * Math.sin(angle) + swayRadius * Math.sin(swayAngle)) * loop.squash;
    out.x = resolveArcPoint(group.from, group.control, group.to, share, 'x')
      + loopX * group.tiltCos - loopY * group.tiltSin;
    out.y = resolveArcPoint(group.from, group.control, group.to, share, 'y')
      + loopX * group.tiltSin + loopY * group.tiltCos;
    return out;
  };

  /**
   * Насколько огонек горит в момент своего полета: мягко проступает у линии
   * деревьев и долго гаснет под конец жизни; вне жизни — ноль
   */
  const resolveShine = (group, flightTimeMs) => {
    if (!(flightTimeMs > 0) || !(flightTimeMs < group.flightMs)) {
      return 0;
    }
    return Math.min(
      smoothstep(0, life.fadeInMs, flightTimeMs), smoothstep(0, group.fadeOutMs, group.flightMs - flightTimeMs)
    );
  };

  /** Мерцание: множитель размера вокруг единицы, две несоизмеримые частоты */
  const resolveTwinkle = (particle, flightTimeMs) => {
    const turns = (TWO_PI * head.twinkleHz * flightTimeMs) / 1000;
    return 1 + head.twinkleDepth * (
      TWINKLE_FIRST_SHARE * Math.sin(turns + particle.twinklePhase)
      + (1 - TWINKLE_FIRST_SHARE) * Math.sin(turns * TWINKLE_SECOND_RATE + particle.twinkleSecondPhase)
    );
  };

  /**
   * Искра пыльцы. Ее место (slot) рождает искру раз в круг: какая искра живет
   * сейчас и когда она родилась — считается из времени
   */
  const resolveDustPose = (group, particle, flightTimeMs, out) => {
    const sinceFirstMs = flightTimeMs - particle.slot * dust.shedEveryMs;
    if (!(sinceFirstMs > 0)) {
      return writeHiddenPose(out);
    }
    const cycle = Math.floor(sinceFirstMs / dustCycleMs);
    const bornMs = particle.slot * dust.shedEveryMs + cycle * dustCycleMs;
    // Искру роняет огонек — в меру того, как он сам горел в этот миг: погасший
    // огонек пыльцы не роняет
    const shine = resolveShine(group, bornMs);
    const ageMs = flightTimeMs - bornMs;
    const lifeMs = lerp(dust.lifeMs.min, dust.lifeMs.max, hash01(particle.seed, cycle));
    if (!(shine > 0) || !(ageMs > 0) || !(ageMs < lifeMs)) {
      return writeHiddenPose(out);
    }
    const lifeShare = ageMs / lifeMs;
    // Родилась там, где огонек был в миг ее рождения, — и остается позади него
    writeFlightPoint(group, bornMs, out);
    // Расплывается в свою сторону, быстро вначале и все медленнее, и оседает
    const spreadAngle = hash01(particle.seed + 0.31, cycle) * TWO_PI;
    const spread = hexSize * dust.spreadFactor
      * lerp(DUST_MIN_SPREAD_SHARE, 1, hash01(particle.seed + 0.57, cycle)) * (1 - (1 - lifeShare) ** 2);
    const x = out.x + Math.cos(spreadAngle) * spread;
    const y = out.y + Math.sin(spreadAngle) * spread + hexSize * dust.fallFactor * lifeShare * lifeShare;
    const twinkle = 1 - dust.twinkleDepth
      * (0.5 + 0.5 * Math.sin((TWO_PI * dust.twinkleHz * ageMs) / 1000 + particle.twinklePhase));
    const size = hexSize * lerp(dust.sizeFactor.min, dust.sizeFactor.max, hash01(particle.seed + 0.83, cycle))
      * lerp(1, dust.endScale, lifeShare);
    return writePose(
      out, x, y,
      dust.alpha * shine * smoothstep(0, dust.fadeInMs, ageMs) * (1 - lifeShare) ** dust.fadePower * twinkle,
      size, size, 0
    );
  };

  // Черновик для точки соседнего пятна шлейфа: пишется и читается внутри одного
  // расчета позы, между расчетами ничего не хранит
  const neighbour = {x: 0, y: 0};

  const resolveFlightPose = (group, particle, elapsedMs, out) => {
    // Время полета самой частицы: шлейф и второй огонек пары живут в прошлом головы
    const flightTimeMs = elapsedMs - particle.lagMs;
    if (particle.kind === DUST) {
      return resolveDustPose(group, particle, flightTimeMs, out);
    }
    const shine = resolveShine(group, flightTimeMs);
    if (!(shine > 0)) {
      return writeHiddenPose(out);
    }
    writeFlightPoint(group, flightTimeMs, out);

    if (particle.kind === TRAIL) {
      // Сосед пятна со стороны головы — та же точка пути чуть позже. На быстром
      // участке петли соседи расходятся, и пятно вытягивается к соседу: иначе
      // шлейф рассыпался бы на бусины
      writeFlightPoint(group, flightTimeMs + trail.stepMs, neighbour);
      const gapX = neighbour.x - out.x;
      const gapY = neighbour.y - out.y;
      return writePose(
        out, out.x, out.y, particle.alpha * shine,
        Math.max(particle.size, Math.hypot(gapX, gapY) * trail.stretch), particle.size,
        Math.atan2(gapY, gapX)
      );
    }
    const twinkle = resolveTwinkle(particle, flightTimeMs);
    if (particle.kind === HALO) {
      const size = hexSize * head.haloSizeFactor * twinkle;
      return writePose(out, out.x, out.y, head.haloAlpha * shine, size, size, 0);
    }
    if (particle.kind === CORE) {
      const size = hexSize * head.coreSizeFactor * twinkle;
      return writePose(out, out.x, out.y, head.coreAlpha * shine, size, size, 0);
    }
    const size = hexSize * head.starSizeFactor * twinkle;
    return writePose(
      out, out.x, out.y, head.starAlpha * shine, size, size,
      particle.spinPhase + particle.spinRadPerMs * flightTimeMs
    );
  };

  // --------------------------------------------------------------- свет леса

  const {breath, wake} = glows;
  // Нарисованные места в мире: картинка пейзажа на месте, и считать их заново незачем
  const spots = glows.spots.map((spot) => ({
    ...context.toWorld(spot.u, spot.v),
    tint: spot.tint,
    size: spot.radius * side * 2 * glows.sizeScale,
  }));

  const buildGlows = ({rng}) => ({
    durationMs: Infinity,
    particles: spots.map((spot) => {
      const wakeEveryMs = randomIn(rng, wake.everyMs);
      return {
        kind: GLOW,
        layer: glows.layer,
        texture: FIELD_BIOME_TEXTURES.DOT,
        tint: spot.tint,
        x: spot.x,
        y: spot.y,
        size: spot.size,
        // У каждого места свой срок вдоха и своя фаза: в такт они не дышат
        breathMs: randomIn(rng, breath.periodMs),
        phase: rng() * TWO_PI,
        secondPhase: rng() * TWO_PI,
        // И свой круг пробуждений, сдвинутый на свою долю
        wakeEveryMs,
        wakeShiftMs: rng() * wakeEveryMs,
        seed: rng() * 1000,
      };
    }),
  });

  /** Насколько место сейчас «проснулось»: 0 — спит, 1 — гребень самого яркого пробуждения */
  const resolveWake = (particle, elapsedMs) => {
    const timeMs = elapsedMs + particle.wakeShiftMs;
    const cycle = Math.floor(timeMs / particle.wakeEveryMs);
    const sinceMs = timeMs - cycle * particle.wakeEveryMs;
    // Просыпается место не на каждом круге: решает число от места и номера круга
    if (!(sinceMs < wake.durationMs) || !(hash01(particle.seed, cycle) < wake.chance)) {
      return 0;
    }
    // Одна плавная волна: 0 -> гребень -> 0
    const wave = Math.sin((Math.PI * sinceMs) / wake.durationMs);
    return wave * wave * lerp(wake.minStrength, 1, hash01(particle.seed + 0.5, cycle));
  };

  const resolveGlowPose = (group, particle, elapsedMs, out) => {
    // Неровное дыхание: две волны с несовпадающими периодами
    const turn = (TWO_PI * elapsedMs) / particle.breathMs;
    const inhale = 0.5 + 0.5 * (
      (1 - breath.secondMix) * Math.sin(turn + particle.phase)
      + breath.secondMix * Math.sin(turn / breath.secondPeriodShare + particle.secondPhase)
    );
    const awake = resolveWake(particle, elapsedMs);
    const size = particle.size * lerp(breath.swellFrom, 1, inhale) * (1 + wake.swell * awake);
    return writePose(
      out, particle.x, particle.y,
      Math.min(glows.maxAlpha, lerp(breath.alpha.min, breath.alpha.max, inhale) + wake.alpha * awake),
      size, size, 0
    );
  };

  return [{
    // Следующий полет — как только эта группа отлетает и выждет свою паузу
    schedule: {
      firstDelayMs: params.firstDelayMs,
      intervalMs: {min: NEXT_FLIGHT_CHECK_MS, max: NEXT_FLIGHT_CHECK_MS},
      maxGroups: 1,
    },
    buildGroup: buildFlight,
    resolvePose: resolveFlightPose,
  }, {
    // Свет стоит на нарисованном: группа одна и живет, пока жив эффект
    schedule: {standing: true},
    buildGroup: buildGlows,
    resolvePose: resolveGlowPose,
  }];
};

const fairyLights = {
  kind: FAIRY_LIGHTS_KIND,
  // Ветер матча эффекту не нужен: при смене ветра его дорожки не трогаются —
  // ни полет, ни свет леса на старте партии не начинаются заново
  usesWind: false,
  textures: [FIELD_BIOME_TEXTURES.DOT, FIELD_BIOME_TEXTURES.SPARK],
  createTracks,
};

export default fairyLights;
